import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Invoice {
  id: string
  customer: string
  amount: number
  status: string
  issue_date: string
  due_date: string
  payment_date: string | null
  days_outstanding: number
}

interface AgingBucket {
  id: string
  bucket: string
  amount: number
  invoice_count: number
  percentage_of_total: number
  expected_collection: number
  risk_amount: number
}

interface CollectionMetric {
  id: string
  month: string
  billed: number
  collected: number
  collection_rate: number
  dso: number
  bad_debt: number
}

interface Statistics {
  total_billed_mtd: number
  total_collected_mtd: number
  ar_balance: number
  dso: number
  collection_rate: number
  overdue_amount: number
}

export default function BillingAnalyticsPage() {
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [aging, setAging] = useState<AgingBucket[]>([])
  const [collections, setCollections] = useState<CollectionMetric[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [invoicesRes, agingRes, collectionsRes, statsRes] = await Promise.all([
        fetch('/api/billinganalytics/invoices'),
        fetch('/api/billinganalytics/aging'),
        fetch('/api/billinganalytics/collections'),
        fetch('/api/billinganalytics/statistics')
      ])
      if (invoicesRes.ok) setInvoices(await invoicesRes.json())
      if (agingRes.ok) setAging(await agingRes.json())
      if (collectionsRes.ok) setCollections(await collectionsRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching billing analytics data:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value)
  }

  const getStatusBadge = (status: string) => {
    if (status === 'paid') return 'bg-green-100 text-green-800'
    if (status === 'pending') return 'bg-yellow-100 text-yellow-800'
    if (status === 'overdue') return 'bg-red-100 text-red-800'
    return 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Billing Analytics</h1>
          <p className="mt-2 text-gray-600">Phase 238 - Billing, invoices, and collections</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Billed MTD</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(statistics.total_billed_mtd)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Collected MTD</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(statistics.total_collected_mtd)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">AR Balance</p>
              <p className="text-2xl font-bold text-blue-600">{formatCurrency(statistics.ar_balance)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">DSO</p>
              <p className="text-2xl font-bold text-purple-600">{statistics.dso} days</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Collection Rate</p>
              <p className="text-2xl font-bold text-indigo-600">{statistics.collection_rate}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Overdue</p>
              <p className="text-2xl font-bold text-red-600">{formatCurrency(statistics.overdue_amount)}</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Recent Invoices</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Issue Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Days Out</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {invoices.map((invoice) => (
                  <tr key={invoice.id}>
                    <td className="px-6 py-4 font-medium text-gray-900">{invoice.id}</td>
                    <td className="px-6 py-4 text-gray-500">{invoice.customer}</td>
                    <td className="px-6 py-4 text-blue-600 font-medium">{formatCurrency(invoice.amount)}</td>
                    <td className="px-6 py-4 text-gray-500">{invoice.issue_date}</td>
                    <td className="px-6 py-4 text-gray-500">{invoice.due_date}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(invoice.status)}`}>{invoice.status}</span>
                    </td>
                    <td className={`px-6 py-4 ${invoice.days_outstanding > 30 ? 'text-red-600' : 'text-gray-500'}`}>{invoice.days_outstanding}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">AR Aging</h2>
            </div>
            <div className="p-6 space-y-4">
              {aging.map((bucket) => (
                <div key={bucket.id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{bucket.bucket}</span>
                    <span className="text-lg font-bold text-blue-600">{formatCurrency(bucket.amount)}</span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full mb-2">
                    <div className="h-2 bg-blue-500 rounded-full" style={{width: `${bucket.percentage_of_total}%`}}></div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Invoices</p>
                      <p className="font-medium">{bucket.invoice_count}</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Expected</p>
                      <p className="font-medium text-green-600">{bucket.expected_collection}%</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">At Risk</p>
                      <p className="font-medium text-red-600">{formatCurrency(bucket.risk_amount)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Collection Metrics</h2>
            </div>
            <div className="p-6 space-y-4">
              {collections.map((metric) => (
                <div key={metric.id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{metric.month}</span>
                    <span className="text-lg font-bold text-green-600">{metric.collection_rate}%</span>
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-sm">
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Billed</p>
                      <p className="font-medium">{formatCurrency(metric.billed)}</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Collected</p>
                      <p className="font-medium text-green-600">{formatCurrency(metric.collected)}</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">DSO</p>
                      <p className="font-medium text-blue-600">{metric.dso}</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Bad Debt</p>
                      <p className="font-medium text-red-600">{formatCurrency(metric.bad_debt)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
